import { FileText, LogOut, Mail, TrendingDown, TrendingUp, Upload as UploadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { ApiError, portal, type PortalClient, type PortalProgress } from "../lib/api";

export default function PortalDashboardPage() {
  const nav = useNavigate();
  const [client, setClient] = useState<PortalClient | null>(null);
  const [progress, setProgress] = useState<PortalProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const c = await portal.me();
        setClient(c);
      } catch (e: any) {
        if (e instanceof ApiError && e.status === 401) {
          nav("/portal/login", { replace: true });
          return;
        }
        setErr(e?.message || "Couldn't load your account.");
        setLoading(false);
        return;
      }
      try {
        const p = await portal.getProgress();
        setProgress(p);
      } catch (e: any) {
        // Non-fatal — show whatever we have.
        setErr(e?.message || null);
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  async function signOut() {
    try {
      await portal.logout();
    } catch {
      // Even if logout fails, drop them at the login screen.
    }
    nav("/portal/login", { replace: true });
  }

  if (loading) {
    return (
      <PortalShell>
        <div className="portal-card">
          <p className="portal-sub on-card">Loading your dashboard…</p>
        </div>
      </PortalShell>
    );
  }

  if (!client) {
    return (
      <PortalShell>
        <div className="portal-card">
          <h2 className="portal-headline on-card" style={{ fontSize: 28 }}>
            We couldn't load your account.
          </h2>
          <p className="portal-sub on-card">{err || "Try signing in again."}</p>
          <Link className="portal-btn-primary wide" to="/portal/login">
            Back to sign-in
          </Link>
        </div>
      </PortalShell>
    );
  }

  const firstName = client.legal_name.split(" ")[0] || "there";
  const p: any = progress ?? {};
  const steps: Array<{ key: string; label: string; done: boolean; in_progress?: boolean }> = p.steps ?? [];
  const readiness = p.readiness ?? null;
  const agreement = p.agreement ?? null;

  return (
    <PortalShell
      actions={
        <button onClick={signOut} aria-label="Sign out">
          <LogOut size={14} aria-hidden="true" /> Sign out
        </button>
      }
    >
      {/* Welcome / page title */}
      <section className="portal-section">
        <p className="portal-eyebrow">Your funding file</p>
        <h1 className="portal-headline">Welcome back, {firstName}.</h1>
        <p className="portal-sub">
          Here's where things stand on your funding journey. Upload anything we
          ask for and we'll keep this page current at every step.
        </p>
      </section>

      {/* 3-card grid: progress / latest letters / documents */}
      <section className="portal-grid-3">
        {/* Card 1 — Funding journey */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Your funding journey</h2>
          </div>
          <div className="portal-list">
            {steps.map((st) => (
              <div className="portal-list-row" key={st.key}>
                <div>
                  <strong>{st.label}</strong>
                </div>
                <span className="meta">
                  {st.done ? "Done" : st.in_progress ? "In progress" : "Up next"}
                </span>
              </div>
            ))}
            {steps.length === 0 && (
              <p className="portal-empty">We're setting up your file — check back shortly.</p>
            )}
          </div>
          {agreement?.sent_at && !agreement?.signed_at && (
            <Link to="/portal/dashboard" className="portal-btn-primary wide" style={{ marginTop: 10 }}
              onClick={(e) => { e.preventDefault(); alert("Open the signature link we emailed you to sign your funding agreement."); }}>
              <Mail size={16} aria-hidden="true" /> Your agreement is waiting for a signature
            </Link>
          )}
        </article>

        {p.builder && (
          <article className="portal-card">
            <div className="portal-card-heading"><h2>Business credit builder</h2></div>
            <div className="portal-stat-row">
              <div className="portal-stat">
                <span className="label">Track progress</span>
                <span className="value" style={{ fontSize: 20 }}>{p.builder.pct}%</span>
              </div>
              <div className="portal-stat">
                <span className="label">Steps done</span>
                <span className="value" style={{ fontSize: 20 }}>{p.builder.done}/{p.builder.total}</span>
              </div>
            </div>
            <p className="portal-sub on-card" style={{ fontSize: 13 }}>
              Your team is walking your business file up the credit ladder — vendor lines, Paydex, then bank cards.
            </p>
          </article>
        )}

        {/* Card 2 — Where you stand */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Where you stand</h2>
          </div>
          {!readiness ? (
            <p className="portal-empty">
              <Mail size={15} aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: 6, opacity: 0.6 }} />
              Your credit analysis isn't ready yet.
            </p>
          ) : (
            <>
              <div className="portal-stat-row">
                <div className="portal-stat">
                  <span className="label">Personal funding</span>
                  <span className="value" style={{ fontSize: 20, color: readiness.personal_eligible ? "var(--ne-success)" : "var(--ne-red-dark)" }}>
                    {readiness.personal_eligible ? "READY" : "NOT YET"}
                  </span>
                </div>
                <div className="portal-stat">
                  <span className="label">Business funding</span>
                  <span className="value" style={{ fontSize: 20, color: readiness.business_eligible ? "var(--ne-success)" : "var(--ne-red-dark)" }}>
                    {readiness.business_eligible ? "READY" : "NOT YET"}
                  </span>
                </div>
              </div>
              {(readiness.checks?.personal?.length > 0) && (
                <div style={{ margin: "10px 0 4px" }}>
                  <span className="portal-sub on-card" style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Personal funding checklist
                  </span>
                  {readiness.checks.personal.map((c: any) => (
                    <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "3px 0", fontSize: 13 }}>
                      <span aria-hidden="true" style={{ color: c.pass ? "var(--ne-success)" : "var(--ne-red-dark)", fontWeight: 700 }}>{c.pass ? "✓" : "✗"}</span>
                      <span style={{ flex: 1 }}>{c.label}</span>
                      <span className="portal-sub on-card" style={{ fontSize: 12 }}>{c.actual} / needs {c.required}</span>
                    </div>
                  ))}
                </div>
              )}
              {(readiness.history?.length > 1) && (
                <p className="portal-sub on-card" style={{ fontSize: 12, marginTop: 8 }}>
                  {(() => {
                    const h = readiness.history;
                    const first = h[0], last = h[h.length - 1];
                    const d = (last?.score ?? 0) - (first?.score ?? 0);
                    const checks = (last?.personal?.passed ?? 0) - (first?.personal?.passed ?? 0);
                    if (!first?.score || !last?.score) return `${h.length} reports analyzed — keep the momentum.`;
                    return `Across ${h.length} reports: score ${d >= 0 ? "up" : "down"} ${Math.abs(d)} points, ${checks >= 0 ? "+" : ""}${checks} checklist items turned green.`;
                  })()}
                </p>
              )}
              {readiness.client_summary && (
                <p className="portal-sub on-card" style={{ fontSize: 13, whiteSpace: "pre-line", maxHeight: 220, overflowY: "auto" }}>
                  {readiness.client_summary}
                </p>
              )}
              {p.offers_enabled && (
                <Link to="/portal/offers" className="portal-btn-primary wide" style={{ marginTop: 10 }}>
                  See your recommended applications
                </Link>
              )}
            </>
          )}
        </article>

        {/* Card 3 — Documents */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Documents</h2>
          </div>
          <p className="portal-sub on-card" style={{ fontSize: 13 }}>
            Need to send us a bank statement, voided check, or ID? Upload it
            here and it lands straight in your file.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <Link to="/portal/upload" className="portal-btn-primary wide">
              <UploadIcon size={16} aria-hidden="true" /> Upload a document
            </Link>
            <Link to="/portal/credit-access" className="portal-btn-ghost wide">
              Update credit logins
            </Link>
          </div>
        </article>
      </section>

      {err && (
        <p className="portal-sub" style={{ fontSize: 12, opacity: 0.8 }}>
          Note: {err}
        </p>
      )}
    </PortalShell>
  );
}
